import { useIsMobile } from "../../hooks/useIsMobile.ts";
import {
	createContext,
	type FC,
	type PropsWithChildren,
	type RefObject,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

export type DateRangeFilter = {
	startDate: Date;
	endDate: Date;
};

type UIContextType = {
	isActive: boolean;
	toggle: (state?: boolean) => void;
	hasScrolledRef: RefObject<boolean>;

	openPanel: () => void;
	closePanel: () => void;

	activeDateRange: DateRangeFilter | null;
	setActiveDateRange: (range: DateRangeFilter | null) => void;
};

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: FC<PropsWithChildren> = ({ children }) => {
	const [isActive, setIsActive] = useState(false);
	const isMobile = useIsMobile();
	const prevIsMobile = useRef(isMobile);
	const hasScrolledRef = useRef(false);

	const [activeDateRange, setActiveDateRange] = useState<DateRangeFilter | null>(null);

	const toggle = (state?: boolean) => {
		if (state !== undefined) {
			setIsActive(state);
		} else {
			setIsActive((prev) => !prev);
		}
	};

	useEffect(() => {
		if (prevIsMobile.current !== isMobile) {
			if (!prevIsMobile.current && isMobile) {
				closePanel();
			}
			prevIsMobile.current = isMobile;
		}
	}, [isMobile]);

	const openPanel = () => {
		setIsActive(true);
	};
	const closePanel = () => {
		setIsActive(false);
	};

	return (
		<UIContext.Provider
			value={{
				isActive,
				toggle,
				hasScrolledRef,
				openPanel,
				closePanel,
				activeDateRange,
				setActiveDateRange,
			}}
		>
			{children}
		</UIContext.Provider>
	);
};

export const useUIContext = () => {
	const context = useContext(UIContext);
	if (!context) throw new Error("useUIContext must be used within a UIProvider");
	return context;
};
